import { useState, useEffect, useCallback } from 'react';
import { Loader2, Play, Pencil, Trash2, Music, Plus, LayoutGrid, List, ShieldCheck, ShieldAlert, ShieldQuestion, AlertTriangle, Lock, Link2, X } from 'lucide-react';
import { listTracks, deleteTrack, connectTrackExternalRelease } from '../services/api';
import { useAuthStore } from '../store/authStore';
import ArtistDashboardNav from '../components/dashboard/ArtistDashboardNav';
import { useOnboardingProgress } from '../hooks/useOnboardingProgress';
import AssetUploadModal, { AssetData } from '../components/dashboard/AssetUploadModal';
import { buildTenantPath } from '../hooks/useTenant';
import { useViewMode } from '../hooks/useViewMode';
import { EnergyVisualizer, generateFallbackEnergyMap } from '../components/EnergyVisualizer';

type VerificationStatus = 'PROCESSING' | 'REJECTED' | 'FLAGGED' | 'VERIFIED' | null;
type ContentIdStatus = 'PENDING' | 'ACCEPTED' | 'REJECTED' | 'ERROR' | 'FAILED' | null;

interface ContentIdMatch {
  artist: string;
  title: string;
  isrc: string | null;
}

interface TrackItem {
  id: string;
  trackReference: string | null;
  title: string;
  isrc?: string | null;
  label?: string | null;
  coverArtUrl: string | null;
  streamUrl: string | null;
  hasAac: boolean;
  verificationStatus: VerificationStatus;
  contentIdStatus: ContentIdStatus;
  passedCompliance: boolean;
  contentIdMatch: ContentIdMatch | null;
  hasLyrics: boolean;
  isPublished: boolean;
  energyMap: number[] | null;
  streamLinks?: Array<{ service: string; serviceTrackId: string | null; url: string; source: string }>;
}

const PLATFORM_ICON_BY_SERVICE: Record<string, { label: string; iconUrl?: string; short: string }> = {
  spotify: { label: 'Spotify', iconUrl: 'https://cdn.simpleicons.org/spotify/1DB954', short: 'SP' },
  applemusic: { label: 'Apple Music', iconUrl: 'https://cdn.simpleicons.org/applemusic/FA243C', short: 'AM' },
  youtube: { label: 'YouTube', iconUrl: 'https://cdn.simpleicons.org/youtube/FF0033', short: 'YT' },
  deezer: { label: 'Deezer', iconUrl: 'https://cdn.simpleicons.org/deezer/A238FF', short: 'DZ' },
  tidal: { label: 'TIDAL', iconUrl: 'https://cdn.simpleicons.org/tidal/FFFFFF', short: 'TD' },
};

function normalizeServiceName(service: string): string {
  return (service || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

function getTrackPlatforms(track: TrackItem): Array<{ key: string; label: string; iconUrl?: string; short: string; url: string }> {
  const links = Array.isArray(track.streamLinks) ? track.streamLinks : [];
  const unique = new Set<string>();
  const results: Array<{ key: string; label: string; iconUrl?: string; short: string; url: string }> = [];

  for (const link of links) {
    const normalized = normalizeServiceName(link.service || '');
    const streamUrl = String(link.url || '').trim();
    if (!normalized || !streamUrl || unique.has(normalized)) continue;
    unique.add(normalized);

    const known = PLATFORM_ICON_BY_SERVICE[normalized];
    if (known) {
      results.push({ key: normalized, label: known.label, iconUrl: known.iconUrl, short: known.short, url: streamUrl });
      continue;
    }

    const short = normalized.slice(0, 2).toUpperCase();
    results.push({ key: normalized, label: link.service || normalized, short: short || '??', url: streamUrl });
  }

  return results.slice(0, 5);
}

function getStatusBadge(status: VerificationStatus) {
  const colors: Record<string, string> = {
    PROCESSING: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    REJECTED: 'bg-red-500/20 text-red-400 border-red-500/30',
    FLAGGED: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    VERIFIED: 'bg-green-500/20 text-green-400 border-green-500/30',
  };
  const labels: Record<string, string> = {
    PROCESSING: 'PROCESSING',
    REJECTED: 'REJECTED',
    FLAGGED: 'IN REVIEW',
    VERIFIED: 'VERIFIED',
  };
  const key = status || 'PENDING';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-mono border ${colors[key] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'}`}>
      {status === 'PROCESSING' && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
      {labels[key] || 'PENDING'}
    </span>
  );
}

function getContentIdBadge(status: ContentIdStatus) {
  if (!status || status === 'PENDING') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono border bg-gray-500/20 text-gray-400 border-gray-500/30">
        <ShieldQuestion className="w-3 h-3 mr-1" />
        SCANNING
      </span>
    );
  }
  if (status === 'ACCEPTED') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono border bg-green-500/20 text-green-400 border-green-500/30">
        <ShieldCheck className="w-3 h-3 mr-1" />
        CLEAR
      </span>
    );
  }
  if (status === 'ERROR' || status === 'FAILED') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono border bg-amber-500/20 text-amber-400 border-amber-500/30">
        <AlertTriangle className="w-3 h-3 mr-1" />
        ERROR
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono border bg-red-500/20 text-red-400 border-red-500/30">
      <ShieldAlert className="w-3 h-3 mr-1" />
      FLAGGED
    </span>
  );
}

function ContentIdWarning({ match }: { match: ContentIdMatch }) {
  return (
    <div className="mt-2 p-2.5 rounded-lg bg-red-500/10 border border-red-500/20">
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-3.5 h-3.5 text-red-400 mt-0.5 flex-shrink-0" />
        <div className="min-w-0">
          <p className="text-xs font-mono text-red-400 font-medium">CONTENT ID MATCH</p>
          <p className="text-xs text-red-300/80 mt-0.5 truncate">
            {match.artist} — {match.title}
          </p>
          {match.isrc && (
            <p className="text-xs text-red-300/60 font-mono mt-0.5">ISRC: {match.isrc}</p>
          )}
        </div>
      </div>
    </div>
  );
}

interface CatalogPageProps {
  slug: string;
}

export default function CatalogPage({ slug }: CatalogPageProps) {
  const { token, logout } = useAuthStore();
  const onboardingProgress = useOnboardingProgress(slug);
  const [tracks, setTracks] = useState<TrackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [viewMode, setViewMode] = useViewMode('catalog_view', 'cards');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [connectModalTrack, setConnectModalTrack] = useState<TrackItem | null>(null);
  const [connectInput, setConnectInput] = useState('');
  const [connectError, setConnectError] = useState<string | null>(null);
  const [connectLoading, setConnectLoading] = useState(false);

  const fetchTracks = useCallback(async () => {
    try {
      const res = await listTracks(slug);
      if (res.ok) {
        const json = await res.json() as { data: TrackItem[] };
        setTracks(json.data);
      }
    } catch (err) {
      console.warn('[CatalogPage] Failed to load tracks:', err);
    }
  }, [slug]);

  useEffect(() => {
    async function load() {
      await fetchTracks();
      setLoading(false);
    }
    load();
  }, [fetchTracks]);

  // Poll for processing tracks or pending content ID scans
  useEffect(() => {
    const hasProcessing = tracks.some((t) => t.verificationStatus === 'PROCESSING' || t.contentIdStatus === 'PENDING');
    if (!hasProcessing) return;

    const interval = setInterval(fetchTracks, 5000);
    return () => clearInterval(interval);
  }, [tracks, fetchTracks]);

  const handleDelete = async (id: string) => {
    setDeleteError(null);
    try {
      const res = await deleteTrack(slug, id, token);
      if (res.ok) {
        setTracks((prev) => prev.filter((t) => t.id !== id));
      } else {
        const errData = await res.json().catch(() => ({ error: 'Delete failed' })) as { error?: string };
        setDeleteError(errData.error || `Failed to delete track (${res.status})`);
      }
    } catch (err) {
      console.warn('[CatalogPage] Failed to delete track:', err);
      setDeleteError('Network error — could not reach server.');
    }
    setDeleteConfirmId(null);
  };

  const openConnectModal = (track: TrackItem) => {
    setConnectModalTrack(track);
    setConnectInput('');
    setConnectError(null);
  };

  const closeConnectModal = () => {
    setConnectModalTrack(null);
    setConnectInput('');
    setConnectError(null);
    setConnectLoading(false);
  };

  const handleConnectRelease = async () => {
    if (!connectModalTrack) return;
    const value = connectInput.trim();
    if (!value) {
      setConnectError('Enter an ISRC or URL.');
      return;
    }

    setConnectLoading(true);
    setConnectError(null);
    try {
      const res = await connectTrackExternalRelease(slug, connectModalTrack.id, { input: value }, token);
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Failed to connect external release' })) as { error?: string; details?: any };
        setConnectError(err.error || 'Failed to connect external release');
        return;
      }

      await fetchTracks();
      closeConnectModal();
    } catch (error) {
      console.warn('[CatalogPage] Failed to connect external release:', error);
      setConnectError('Network error while connecting external release.');
    } finally {
      setConnectLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-deep-charcoal flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-electric-cyan animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-deep-charcoal">
      <ArtistDashboardNav slug={slug} currentPage="catalog" onboardingProgress={onboardingProgress} />

      <main className="pt-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold">Track Catalog</h1>
            <p className="text-sm text-text-secondary mt-1 font-mono">Manage your uploaded tracks</p>
          </div>
          <div className="flex items-center gap-3">
            {/* View Mode Toggle */}
            <div className="flex items-center bg-white/5 border border-white/10 rounded-lg p-0.5">
              <button
                onClick={() => setViewMode('cards')}
                className={`p-1.5 rounded transition-colors cursor-pointer ${viewMode === 'cards' ? 'bg-electric-cyan/20 text-electric-cyan' : 'text-text-muted hover:text-white'}`}
                title="Card view"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`p-1.5 rounded transition-colors cursor-pointer ${viewMode === 'table' ? 'bg-electric-cyan/20 text-electric-cyan' : 'text-text-muted hover:text-white'}`}
                title="Table view"
              >
                <List className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-electric-cyan/10 text-electric-cyan border border-electric-cyan/30 text-sm font-medium hover:bg-electric-cyan/20 transition-colors cursor-pointer"
            >
              <Plus className="w-4 h-4" />
              Upload Track
            </button>
          </div>
        </div>

        {/* Delete Error Banner */}
        {deleteError && (
          <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center justify-between">
            <span className="text-sm text-red-400">{deleteError}</span>
            <button onClick={() => setDeleteError(null)} className="text-red-400 hover:text-red-300 text-xs font-mono cursor-pointer">DISMISS</button>
          </div>
        )}

        {/* Content */}
        {tracks.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <Music className="w-12 h-12 text-electric-cyan/30 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No tracks yet</h3>
            <p className="text-text-secondary text-sm mb-6">Click Upload Track to add your first track.</p>
          </div>
        ) : viewMode === 'cards' ? (
          /* Card View */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {tracks.map((track) => (
              (() => {
                const platforms = getTrackPlatforms(track);
                return (
              <div
                key={track.id}
                className="glass-card-hover rounded-xl overflow-hidden flex flex-col"
              >
                {/* Waveform */}
                <div className="relative aspect-[2/1] bg-white/5 flex items-center justify-center p-4">
                  {track.energyMap ? (
                    <EnergyVisualizer
                      energyMap={track.energyMap}
                      primaryColor="#00FFFF"
                      backgroundColor="rgba(0,255,255,0.3)"
                      height="90%"
                    />
                  ) : track.verificationStatus === 'PROCESSING' ? (
                    <div className="w-full h-[90%] flex items-center justify-center">
                      <EnergyVisualizer
                        energyMap={generateFallbackEnergyMap(track.id)}
                        primaryColor="rgba(0,255,255,0.2)"
                        backgroundColor="rgba(0,255,255,0.1)"
                        height="100%"
                        className="opacity-40 animate-pulse"
                      />
                    </div>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Music className="w-12 h-12 text-text-muted/30" />
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-4 flex flex-col flex-1">
                  <h3 className="font-semibold text-white text-sm truncate mb-1">{track.title}</h3>
                  <p className="font-mono text-xs text-text-secondary mb-3 truncate">
                    {track.trackReference || '—'}
                  </p>
                  {/* Status rows */}
                  <div className="flex flex-col gap-1.5 mb-3">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-mono uppercase tracking-wider text-text-muted">AI Verification</span>
                      {getStatusBadge(track.verificationStatus)}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-mono uppercase tracking-wider text-text-muted">Content ID</span>
                      {getContentIdBadge(track.contentIdStatus)}
                    </div>
                  </div>
                  {/* Content ID warning */}
                  {track.contentIdStatus === 'REJECTED' && track.contentIdMatch && (
                    <ContentIdWarning match={track.contentIdMatch} />
                  )}
                  <div className="mt-auto pt-3 border-t border-white/5 flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-mono text-xs text-text-muted">
                        {track.hasAac ? 'AAC' : 'WAV'}
                      </span>
                      {platforms.length > 0 && (
                        <div className="flex items-center gap-1.5">
                          {platforms.map((platform) => (
                            <a
                              key={platform.key}
                              href={platform.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-4 h-4 rounded-[4px] overflow-hidden bg-white/10 border border-white/15 flex items-center justify-center"
                              title={`Open on ${platform.label}`}
                            >
                              {platform.iconUrl ? (
                                <img
                                  src={platform.iconUrl}
                                  alt={platform.label}
                                  className="w-full h-full object-contain"
                                  loading="lazy"
                                />
                              ) : (
                                <span className="text-[8px] font-bold text-white/80 leading-none">{platform.short}</span>
                              )}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {/* Published lock indicator */}
                      {track.isPublished && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono bg-amber-500/10 text-amber-400 border border-amber-500/20" title="Published in a pressing — locked">
                          <Lock className="w-3 h-3" /> MINTED
                        </span>
                      )}
                      {/* Edit button */}
                      {!track.isPublished && track.verificationStatus === 'VERIFIED' && track.hasLyrics && track.trackReference && (
                        <button
                          onClick={() => { window.location.href = buildTenantPath(slug, `track-editor/${track.trackReference}`); }}
                          className="p-1.5 rounded hover:bg-electric-cyan/10 transition-colors cursor-pointer"
                          title="Edit track"
                        >
                          <Pencil className="w-3.5 h-3.5 text-text-muted hover:text-electric-cyan" />
                        </button>
                      )}
                      {/* Play button */}
                      {track.streamUrl && track.verificationStatus === 'VERIFIED' && (
                        <button
                          onClick={() => { window.location.href = buildTenantPath(slug, `player/${track.trackReference}`); }}
                          className="p-1.5 rounded hover:bg-electric-cyan/10 transition-colors cursor-pointer"
                          title="Play track"
                        >
                          <Play className="w-3.5 h-3.5 text-electric-cyan" />
                        </button>
                      )}
                      {/* Connect External Release */}
                      {!track.isrc && (
                        <button
                          onClick={() => openConnectModal(track)}
                          className="p-1.5 rounded hover:bg-electric-cyan/10 transition-colors cursor-pointer"
                          title="Connect External Release"
                        >
                          <Link2 className="w-3.5 h-3.5 text-text-muted hover:text-electric-cyan" />
                        </button>
                      )}
                      {/* Delete button — hidden for published tracks */}
                      {!track.isPublished && (
                        deleteConfirmId === track.id ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleDelete(track.id)}
                            className="px-2 py-0.5 rounded text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30 transition-colors cursor-pointer"
                          >
                            Yes
                          </button>
                          <button
                            onClick={() => setDeleteConfirmId(null)}
                            className="px-2 py-0.5 rounded text-xs font-medium text-text-muted hover:text-white transition-colors cursor-pointer"
                          >
                            No
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirmId(track.id)}
                          className="p-1.5 rounded hover:bg-red-500/10 transition-colors cursor-pointer"
                          title="Delete track"
                        >
                          <Trash2 className="w-3.5 h-3.5 text-text-muted hover:text-red-400" />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
                );
              })()
            ))}
          </div>
        ) : (
          /* Table View */
          <div className="glass-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left px-4 py-3 font-mono text-xs uppercase tracking-wider text-text-secondary w-12"></th>
                    <th className="text-left px-4 py-3 font-mono text-xs uppercase tracking-wider text-text-secondary">AI Verification</th>
                    <th className="text-left px-4 py-3 font-mono text-xs uppercase tracking-wider text-text-secondary">Content ID</th>
                    <th className="text-left px-4 py-3 font-mono text-xs uppercase tracking-wider text-text-secondary">Title</th>
                    <th className="text-left px-4 py-3 font-mono text-xs uppercase tracking-wider text-text-secondary">Reference</th>
                    <th className="text-left px-4 py-3 font-mono text-xs uppercase tracking-wider text-text-secondary">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tracks.map((track) => (
                    <tr
                      key={track.id}
                      className="border-b border-white/5 hover:bg-white/5 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="w-20 h-10 min-w-[5rem] min-h-[2.5rem] rounded bg-white/5 flex items-center justify-center overflow-hidden">
                          {track.energyMap ? (
                            <EnergyVisualizer
                              energyMap={track.energyMap}
                              primaryColor="#00FFFF"
                              backgroundColor="rgba(0,255,255,0.3)"
                              height="90%"
                            />
                          ) : track.verificationStatus === 'PROCESSING' ? (
                            <EnergyVisualizer
                              energyMap={generateFallbackEnergyMap(track.id)}
                              primaryColor="rgba(0,255,255,0.2)"
                              backgroundColor="rgba(0,255,255,0.1)"
                              height="90%"
                              className="opacity-40 animate-pulse"
                            />
                          ) : (
                            <Music className="w-4 h-4 text-text-muted" />
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {getStatusBadge(track.verificationStatus)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          {getContentIdBadge(track.contentIdStatus)}
                          {track.contentIdStatus === 'REJECTED' && track.contentIdMatch && (
                            <span className="text-xs text-red-400 truncate max-w-[200px]" title={`${track.contentIdMatch.artist} — ${track.contentIdMatch.title}`}>
                              {track.contentIdMatch.artist} — {track.contentIdMatch.title}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 font-medium text-white">
                        {track.title}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-text-secondary whitespace-nowrap">
                        {track.trackReference || '—'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          {/* Published lock indicator */}
                          {track.isPublished && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono bg-amber-500/10 text-amber-400 border border-amber-500/20" title="Published in a pressing — locked">
                              <Lock className="w-3 h-3" /> MINTED
                            </span>
                          )}
                          {/* Edit button */}
                          {!track.isPublished && track.verificationStatus === 'VERIFIED' && track.hasLyrics && track.trackReference && (
                            <button
                              onClick={() => { window.location.href = buildTenantPath(slug, `track-editor/${track.trackReference}`); }}
                              className="p-1.5 rounded hover:bg-electric-cyan/10 transition-colors"
                              title="Edit track"
                            >
                              <Pencil className="w-4 h-4 text-text-muted hover:text-electric-cyan" />
                            </button>
                          )}

                          {/* Play button */}
                          {track.streamUrl && track.verificationStatus === 'VERIFIED' && (
                            <button
                              onClick={() => { window.location.href = buildTenantPath(slug, `player/${track.trackReference}`); }}
                              className="p-1.5 rounded hover:bg-electric-cyan/10 transition-colors"
                              title="Play track"
                            >
                              <Play className="w-4 h-4 text-electric-cyan" />
                            </button>
                          )}
                          {/* Connect External Release */}
                          {!track.isrc && (
                            <button
                              onClick={() => openConnectModal(track)}
                              className="p-1.5 rounded hover:bg-electric-cyan/10 transition-colors"
                              title="Connect External Release"
                            >
                              <Link2 className="w-4 h-4 text-text-muted hover:text-electric-cyan" />
                            </button>
                          )}

                          {/* Delete button — hidden for published tracks */}
                          {!track.isPublished && (
                          deleteConfirmId === track.id ? (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleDelete(track.id)}
                                className="px-2.5 py-1 rounded text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30 transition-colors"
                              >
                                Confirm
                              </button>
                              <button
                                onClick={() => setDeleteConfirmId(null)}
                                className="px-2.5 py-1 rounded text-xs font-medium text-text-muted hover:text-white transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setDeleteConfirmId(track.id)}
                              className="p-1.5 rounded hover:bg-red-500/10 transition-colors"
                              title="Delete track"
                            >
                              <Trash2 className="w-4 h-4 text-text-muted hover:text-red-400" />
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {/* Upload Modal */}
        <AssetUploadModal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          slug={slug}
          onUploadComplete={(asset: AssetData) => {
            setTracks((prev) => {
              if (prev.some((t) => t.id === asset.id)) return prev;
              return [...prev, {
                id: asset.id,
                trackReference: asset.trackReference,
                title: asset.title,
                coverArtUrl: asset.coverArtUrl,
                energyMap: null,
                streamUrl: null,
                hasAac: false,
                verificationStatus: 'PROCESSING' as VerificationStatus,
                contentIdStatus: 'PENDING' as ContentIdStatus,
                passedCompliance: false,
                contentIdMatch: null,
                hasLyrics: false,
                isPublished: false,
              }];
            });
          }}
        />

        {/* Connect External Release Modal */}
        {connectModalTrack && (
          <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="w-full max-w-lg rounded-xl border border-white/10 bg-[#121212] shadow-2xl">
              <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
                <div>
                  <h3 className="text-base font-semibold text-white">Connect External Release</h3>
                  <p className="text-xs text-text-secondary mt-1">{connectModalTrack.title}</p>
                </div>
                <button
                  onClick={closeConnectModal}
                  className="p-1.5 rounded hover:bg-white/10 transition-colors"
                  title="Close"
                >
                  <X className="w-4 h-4 text-text-muted" />
                </button>
              </div>

              <div className="px-5 py-4 space-y-3">
                <p className="text-sm text-text-secondary">
                  Enter an ISRC or streaming URL. fans.audio will look up metadata and connect available streaming services.
                </p>
                <input
                  value={connectInput}
                  onChange={(e) => setConnectInput(e.target.value)}
                  placeholder="ISRC (e.g. CAHQJ2686552) or URL"
                  className="w-full px-3 py-2.5 rounded-lg bg-black/40 border border-white/15 text-sm text-white placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-electric-cyan/50"
                />
                {connectError && (
                  <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
                    {connectError}
                  </div>
                )}
              </div>

              <div className="px-5 py-4 border-t border-white/10 flex justify-end gap-2">
                <button
                  onClick={closeConnectModal}
                  disabled={connectLoading}
                  className="px-4 py-2 rounded-lg border border-white/15 text-sm text-text-secondary hover:text-white hover:border-white/30 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConnectRelease}
                  disabled={connectLoading}
                  className="px-4 py-2 rounded-lg bg-electric-cyan/15 border border-electric-cyan/40 text-sm text-electric-cyan hover:bg-electric-cyan/25 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {connectLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                  Connect External Release
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
